import { useEffect, useState } from 'react';
import { Button, Field, Modal, TextInput } from '@salonomy/ui';

/**
 * Destructive-action confirmation (WS6 non-negotiable). When `confirmPhrase` is set the user must
 * type it exactly before the action arms — used for deactivation and (later) GDPR erasure. State
 * the consequences in `children`; the action is online-only with explicit success at the call site.
 */
export function ConfirmModal({
  open,
  title,
  confirmLabel,
  confirmPhrase,
  tone = 'danger',
  busy,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  /** If set, the user must type this exact string (e.g. the member's name) to enable the action. */
  confirmPhrase?: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const [typed, setTyped] = useState('');
  const armed = !confirmPhrase || typed.trim() === confirmPhrase.trim();

  // Clear the typed phrase whenever the dialog closes, so a reopen (for a different target) starts
  // blank — this component stays mounted while the parent only toggles `open`.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  function close() {
    setTyped('');
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            disabled={!armed || busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{children}</div>
      {confirmPhrase ? (
        <Field
          label={`Type “${confirmPhrase}” to confirm`}
          hint="This guards against accidental clicks."
        >
          <TextInput
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmPhrase}
          />
        </Field>
      ) : null}
    </Modal>
  );
}
